import { useEffect } from "react";
import { SITE_URL } from "./data";

// Lightweight per-page SEO hook. Sets document title and manages standard
// + Open Graph + Twitter + canonical meta tags imperatively (no extra deps).
export function useSEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  jsonLd, // optional object or array of objects to inject as JSON-LD
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const canonical = `${SITE_URL}${path}`;
    const ogImage = image || `${SITE_URL}/og-default.png`;

    const tags = [
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:url", content: canonical },
      { property: "og:image", content: ogImage },
      { property: "og:site_name", content: "Act Fast Appliance Repair" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ];

    const setMeta = (selector, attrName, value, content) => {
      if (!content) return null;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, value);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const created = [];
    tags.forEach((t) => {
      const isProperty = "property" in t;
      const key = isProperty ? t.property : t.name;
      const attr = isProperty ? "property" : "name";
      const selector = `meta[${attr}="${key}"]`;
      const el = setMeta(selector, attr, key, t.content);
      if (el) created.push(el);
    });

    // Canonical link
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonical);

    // Page-specific JSON-LD
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute("data-page-jsonld", "true");
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      // Remove page-specific JSON-LD only; leave meta tags (next page will overwrite).
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, JSON.stringify(jsonLd)]);
}
